import { useMemo, useEffect, useRef } from 'react';
import {
  useGetChatsQuery,
  useCreateMessageMutation,
  useCreateChatMutation,
} from '@teachur/http-client';
import { toast } from 'react-toastify';
import type { Chat } from '@teachur/http-client';

interface UseVideoCallChatOptions {
  currentUserId: number | undefined;
  studentId: number | undefined;
  instructorId: number | undefined;
}

export const useVideoCallChat = ({
  currentUserId,
  studentId,
  instructorId,
}: UseVideoCallChatOptions) => {
  const { data: chatsResponse, isLoading: isChatsLoading } = useGetChatsQuery({
    limit: 50,
    offset: 0,
  });
  const allChats = chatsResponse?.data || [];
  const createChat = useCreateChatMutation({
    onError: () => toast.error('Failed to create chat.'),
  });
  const autoCreateAttempted = useRef(false);

  const chat = useMemo<Chat | undefined>(() => {
    if (!allChats) return undefined;
    return allChats.find(
      (c: Chat) =>
        c.users.some((u) => u.id === studentId) && c.users.some((u) => u.id === instructorId)
    );
  }, [allChats, studentId, instructorId]);

  useEffect(() => {
    if (
      !isChatsLoading &&
      !chat &&
      studentId &&
      instructorId &&
      currentUserId &&
      !autoCreateAttempted.current
    ) {
      autoCreateAttempted.current = true;
      const otherUserId = currentUserId === instructorId ? studentId : instructorId;
      createChat.mutate({ userId: otherUserId, text: 'Video call chat' });
    }
  }, [isChatsLoading, chat, studentId, instructorId, currentUserId]);

  const createMessage = useCreateMessageMutation(chat?.id ?? 0);

  const handleSendMessage = (text: string) => {
    if (!chat?.id || !currentUserId) return;
    const recipient = chat.users.find((u) => u.id !== currentUserId);
    if (!recipient) return;
    createMessage.mutate({ text, sendChatUserId: recipient.id });
  };

  return {
    chat,
    chatId: chat?.id,
    currentUserId,
    isLoading: isChatsLoading || (!chat && createChat.isPending),
    isSending: createMessage.isPending,
    sendMessage: handleSendMessage,
  };
};
